import { useQuery, useQueryClient } from '@tanstack/react-query';
import { http, legacy } from '@/shared/api/client';

export type CatalogKind = 'module' | 'cargo' | 'pdv';

export interface CatalogItem {
  _id: string;
  kind: CatalogKind;
  name: string;
  active: boolean;
  order: number;
}

export async function fetchCatalog(kind: CatalogKind): Promise<CatalogItem[]> {
  const data = await legacy.get<CatalogItem[]>(`/catalog/${kind}`);
  return Array.isArray(data) ? data : [];
}

/** Catálogo ativo sem autenticação (form público de cadastro). */
export async function fetchPublicCatalog(kind: CatalogKind): Promise<CatalogItem[]> {
  const data = await http.get<CatalogItem[]>(`/bff/public/catalog/${kind}`);
  return Array.isArray(data) ? data : [];
}

export interface CatalogItemPayload {
  action: 'insert' | 'edit' | 'deleted';
  id?: string;
  kind: CatalogKind;
  name?: string;
  active?: boolean;
  order?: number;
}

export async function saveCatalogItem(payload: CatalogItemPayload): Promise<CatalogItem> {
  return legacy.post<CatalogItem>('/catalog', payload);
}

export function useCatalog(kind: CatalogKind) {
  return useQuery({
    queryKey: ['catalog', kind],
    queryFn: () => fetchCatalog(kind),
    staleTime: 5 * 60 * 1000,
  });
}

/** Cria (ou reaproveita, se já existir) um item do catálogo e atualiza o cache. */
export function useCreateCatalogItem(kind: CatalogKind) {
  const queryClient = useQueryClient();
  return async (name: string) => {
    const created = await saveCatalogItem({ action: 'insert', kind, name });
    queryClient.setQueryData<CatalogItem[]>(['catalog', kind], (prev) => {
      if (!prev) return [created];
      if (prev.some((item) => item._id === created._id)) return prev;
      return [...prev, created];
    });
    return created;
  };
}
